<template>
    <div>
        <v-col v-if="!loading && patientId" cols="12" no-gutters class="patient-heading">
            <div>
                <patient-latest-blood-results></patient-latest-blood-results>
            </div>
        </v-col>
        <v-col v-if="!loading" cols="12" no-gutters>
            <div class="tabs double-fix">
                <b-alert
                    v-if="hasChangeRequest"
                    type="info"
                    :message="t('patients::messages.has_change_request')"
                    :has-header-button="can('api.v1.requests.show')"
                    :button-title="t('patients::buttons.show_changes')"
                    :dismissible="false"
                    @emitAction="showChangeRequestDialog = true"
                >
                </b-alert>
                <patient-details-tabs></patient-details-tabs>
                <patient-details-tabs-items
                    :id="id"
                    :has-change-request="hasChangeRequest"
                    :preview="preview || (hasChangeRequest && isHcp)"
                ></patient-details-tabs-items>
            </div>
        </v-col>

        <patient-change-request-dialog
            :change-request-id="getChangeRequestId"
            :show="showChangeRequestDialog"
            @close="showChangeRequestDialog = false"
        ></patient-change-request-dialog>

        <v-row v-if="loading">
            <v-col cols="12">
                <v-progress-circular class="d-block m-auto" indeterminate color="primary"></v-progress-circular>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { initialPatientsState } from '../store/Patients';
import BAlert from '../../../../../bgo/bgo-components/resources/js/components/BAlert.vue';

export default {
    components: { BAlert },
    props: {
        id: {
            type: String,
            default: null,
        },
        tab: {
            type: String,
            default: null,
            required: false,
        },
        preview: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            showChangeRequestDialog: false,
            changeRequestData: [],
        };
    },
    computed: {
        selectedTab() {
            this.loadPatient();
            return this.$store.state.patients.tab;
        },
        patientId() {
            return this.$store.state.patients.patient.id;
        },
        hasChangeRequest() {
            return !!this.$store.state.patients.patient.change_request_id;
        },
        getChangeRequestId() {
            return this.$store.state.patients.patient.change_request_id ?? '';
        },
        isHcp() {
            return User.group === 'hcps';
        },
    },
    watch: {
        selectedTab(val) {
            if (val !== 6) {
                const url = new URL(window.location);
                url.searchParams.delete('blood_result_id');
                window.history.replaceState(null, '', url);
            }
        },
    },
    created() {
        this.$store.dispatch('patients/loadBloodProvidersOptions');
        this.$store.dispatch('patients/loadPatientStatusOptions');
        this.$store.dispatch('patients/loadCCOptions');
        this.$store.dispatch('patients/loadPharmacyOptions');
        this.$store.dispatch('patients/loadConsultantOptions');
        this.loadPatient();
    },
    mounted() {
        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.get('pdf')) {
            this.loadPatientFormPdf();
        }
    },
    methods: {
        loadPatientFormPdf() {
            const patientData = _.cloneDeep(localStorage.patient) || false;
            if (!patientData) {
                return this.$root.goTo(route('upload-from-pdf.index'));
            }
            this.loading = true;
            this.$store.dispatch('patients/loadPatient', JSON.parse(patientData));
            this.$store.dispatch('patients/loadAddress');
            _.delay(() => {
                this.loading = false;
            }, 500);
            return;
        },
        loadPatient() {
            const urlParams = new URLSearchParams(window.location.search);

            if (_.isEmpty(this.id)) {
                const pharmacySiteId = urlParams.get('pharmacy_site_id');

                if (pharmacySiteId) {
                    this.$store.dispatch('patients/setPharmacySite', { value: pharmacySiteId });
                }

                this.loading = false;

                return;
            }

            axios
                .get(
                    route('api.v1.patients.show', {
                        patient: this.id,
                        fields: [
                            '*',
                            'dispensing',
                            'has_treatment_break',
                            'diagnosis.*',
                            'health_information.*',
                            'ward_information.*',
                            'color',
                            'banner_color',
                            'validity_date',
                            'name',
                            'first_blood_sample_date',
                            'last_blood_sample_date',
                            'last_blood_sample_color',
                            'next_due_date',
                            'blood_testing_frequency',
                            'prohibited',
                            'max_dispensing',
                            'overdue',
                            'is_abnormal_last_sample',
                        ].join(','),
                    })
                )
                .then((response) => {
                    if (!response) {
                        return;
                    }

                    const data = response.data.data;
                    data.dispensing.timeLine = initialPatientsState.patient.dispensing.timeLine;
                    data.dispensing.timeLine[0].patient_id = this.id;

                    this.$store.dispatch('patients/loadPatient', data);
                    this.$store.dispatch('patients/loadAddress');

                    const isNewBloodResult = urlParams.get('new_blood_result');

                    const loadBloodResult = urlParams.get('load_lood_result');

                    const bloodResultId = urlParams.get('blood_result_id');

                    const isNewAdverseEvent = urlParams.get('new_adverse_event');

                    if (loadBloodResult) {
                        this.loadBloodResult(bloodResultId);
                    }

                    if (isNewBloodResult) {
                        this.$store.dispatch('patients/newBloodResult');
                    }

                    if (isNewAdverseEvent) {
                        this.$store.dispatch('patients/changeTab', 6);
                        this.$store.dispatch('adverse_event/newAdverseEvent', { id: bloodResultId });
                    }
                })
                .catch((error) => {
                    this.$root.notify('error', error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadBloodResult(bloodResultId) {
            this.$store.dispatch('patients/changeTab', 4);
            this.$store.dispatch('patients/resetErrorMessages');
            axios
                .get(
                    route('api.v1.patients.blood-results.show', {
                        patient: this.id,
                        blood_result: bloodResultId,
                        fields: '*,blood_result_provider_id,operator,editable,rejected,blood_result_provider.id,blood_result_provider.name,blood_result_provider.visible_in_qc_list,provider_type',
                    })
                )
                .then((response) => {
                    if (!response) {
                        return;
                    }

                    const data = response.data.data;
                    this.$store.dispatch('patients/loadBloodResult', data);
                });
        },
    },
};
</script>
